import {
  AdminCarbonCrop,
  carbonCropsEndpoints,
} from "portal/state/portal/carbonCrops";
import {
  adminCarbonCropsReadStatement,
  useCanUpdateAdminCarbonCrops,
} from "portal/views/admin/permissions";
import { asBoolean, asString, useQuery } from "portal/utils/hooks/useQuery";
import { CARBON_CROP_TRANSLATION_LANGUAGES } from "./languages";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { CarbonListCell } from "portal/components/table/CarbonListCell";
import { Checkbox, FormControlLabel } from "@mui/material";
import { classes } from "portal/theme/theme";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { debounce } from "portal/utils/timing";
import { getAdminCarbonCropPath } from "portal/utils/routing";
import { GridColDef } from "@mui/x-data-grid-premium";
import { NavigateOptions, useNavigate } from "react-router-dom";
import { NewCarbonCrop } from "./NewCarbonCrop";
import { NoScroll } from "portal/components/Page";
import { portalEndpoints } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import { toDisplayName } from "common/utils/i18n";
import { UnassociatedVeselkaCropsWarning } from "./UnassociatedVeselkaCropsWarning";
import { useLatest } from "common/hooks/useLatest";
import { useLazyPopups, useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { veselkaCropAssociationName } from "./VeselkaCropLabel";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import CarbonCropIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";
import VeselkaIcon from "portal/images/icons/veselka.svg?react";

const defaultColumn: Partial<GridColDef<AdminCarbonCrop>> = {
  disableColumnMenu: true,
  flex: 1,
};

//Replace so debounced search updates do not add history entries.
const replaceSearchQuery: NavigateOptions = { replace: true };

const _AdminCarbonCropList: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canUpdate = useCanUpdateAdminCarbonCrops();
  const [searchText = "", setSearchText] = useQuery(
    "search",
    asString,
    replaceSearchQuery
  );
  const [searchInput, setSearchInput] = useState(searchText);
  // The query setter closes over the current search params; read it through a
  // ref so the debounced call doesn't clobber params changed since mount.
  const setSearchTextRef = useLatest(setSearchText);
  const [includeArchived = false, setIncludeArchived] = useQuery(
    "includeArchived",
    asBoolean
  );
  const [listCarbonCrops] = useLazyPopups(
    carbonCropsEndpoints.listCarbonCrops.useLazyQuery()
  );

  const { data: globalCrops, isLoading: isLoadingGlobalCrops } = useQueryPopups(
    portalEndpoints.listCrops.useQuery()
  );

  const fetchPage = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<AdminCarbonCrop>> => {
      const { crops, nextPageToken } = await listCarbonCrops({
        limit,
        pageToken: cursor,
        search: searchText.trim() || undefined,
        ...(includeArchived ? { includeArchived: true } : {}),
      }).unwrap();
      return {
        rows: crops,
        pageInfo: {
          hasNextPage: Boolean(nextPageToken),
          nextCursor: nextPageToken || undefined,
        },
      };
    },
    [includeArchived, listCarbonCrops, searchText]
  );

  const setDebouncedSearchText = useMemo(
    () =>
      debounce((text: string) =>
        setSearchTextRef.current(text.trim() ? text : undefined)
      ),
    [setSearchTextRef]
  );

  const { dataGridProps, reset } =
    useCursorDataSource<AdminCarbonCrop>(fetchPage);

  const onSearchInputChange = useCallback(
    (text: string) => {
      setSearchInput(text);
      setDebouncedSearchText(text);
    },
    [setDebouncedSearchText]
  );

  const columns = useMemo((): GridColDef<AdminCarbonCrop>[] => {
    return [
      {
        ...defaultColumn,
        field: "name",
        flex: 2,
        minWidth: 280,
        headerName: t("models.carbonCrops.fields.name"),
        renderCell: ({ row }) => {
          const archived = Boolean(row.archivedAt);
          const name = archived
            ? t("models.carbonCrops.fields.archivedName", { name: row.name })
            : row.name;
          return (
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
              <CarbonCropIcon
                className="shrink-0"
                color="inherit"
                fontSize="small"
              />
              <span className={classes("truncate", archived && "italic")}>
                {name}
              </span>
            </div>
          );
        },
      },
      {
        ...defaultColumn,
        field: "veselkaCropIds",
        minWidth: 120,
        headerName: t("models.carbonCrops.fields.veselkaCrops"),
        renderCell: ({ value: veselkaCropIds }) => (
          <CarbonListCell
            loading={isLoadingGlobalCrops}
            icon={<VeselkaIcon className="ml-2 shrink-0" />}
            values={(veselkaCropIds ?? []).map((id: string) => {
              const crop = globalCrops?.[id];
              return {
                id,
                name: crop ? veselkaCropAssociationName(t, crop) : id,
              };
            })}
          />
        ),
      },
      ...CARBON_CROP_TRANSLATION_LANGUAGES.map(
        (language): GridColDef<AdminCarbonCrop> => ({
          ...defaultColumn,
          field: `translation:${language}`,
          headerName: toDisplayName(language),
          valueGetter: (_value, row) =>
            row.translations.find((entry) => entry.language === language)
              ?.translation ?? "",
        })
      ),
    ];
  }, [t, globalCrops, isLoadingGlobalCrops]);

  return (
    <NoScroll>
      <div className="flex flex-col h-full">
        <UnassociatedVeselkaCropsWarning className="mb-4" />
        <CarbonDataGrid<AdminCarbonCrop>
          {...dataGridProps}
          header={
            <>
              <div className="flex items-center gap-2 min-w-0">
                <SearchField
                  value={searchInput}
                  onChange={onSearchInputChange}
                  label={t("utils.actions.searchLong", {
                    subject: t("models.carbonCrops.carbonCrop_plural"),
                  })}
                />
                <FormControlLabel
                  className="flex-shrink-0"
                  control={
                    <Checkbox
                      checked={includeArchived}
                      color="default"
                      onChange={(_event, checked) =>
                        setIncludeArchived(checked ? true : undefined)
                      }
                    />
                  }
                  label={
                    <span className="whitespace-nowrap">
                      <span className="hidden sm:inline">
                        {t("utils.actions.showLong", {
                          subject: t("models.carbonCrops.fields.archived"),
                        })}
                      </span>
                      <span className="sm:hidden">
                        {t("models.carbonCrops.fields.archived")}
                      </span>
                    </span>
                  }
                />
              </div>
              {canUpdate && <NewCarbonCrop hotkey="n" onCreated={reset} />}
            </>
          }
          className="flex flex-1"
          getRowId={(crop) => crop.uuid}
          columns={columns}
          pinnedColumns={{ left: ["name"] }}
          onRowClick={({ row }) => navigate(getAdminCarbonCropPath(row.uuid))}
          disableRowSelectionOnClick
          getRowClassName={() => "cursor-pointer"}
        />
      </div>
    </NoScroll>
  );
};

export const AdminCarbonCropList = withAuthorizationRequired(
  adminCarbonCropsReadStatement,
  _AdminCarbonCropList
);
